// CreateProductModal.js
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Input,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Inventory,
  CloudUpload,
} from "@mui/icons-material";
import { message } from "antd";
import axios from "axios";
// import "./index.scss";

const SelfCreateProductModal = ({
  open,
  onClose,
  onSuccess,
  selectedVendor,
  selectedCategory,
  storeId,
}) => {
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !price || !quantity) {
      message.error("Please fill in all required fields");
      return;
    }

    if (!selectedVendor || !selectedCategory) {
      message.error("Please select vendor and category first");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        storefront: storeId,
        image_urls: [],
        vendor: selectedVendor.name || selectedVendor.title,
        category_id: selectedCategory.id,
        category_name: selectedCategory.name,
        title: title.trim(),
        price: parseFloat(price),
        quantity: parseInt(quantity),
        source: "retail_form",
      };

      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_BACKEND}/storefront/b2b_product_creation`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Check if response has detail: "Success" regardless of status code
      if (response.data?.detail === "Success" || response.data) {
        onSuccess(response.data);
        // Reset form
        setTitle("");
        setPrice("");
        setQuantity("");
        setSelectedFile(null);
      }
    } catch (error) {
      // Check if error response has detail: "Success"
      if (error.response?.data?.detail === "Success") {
        onSuccess(error.response.data);
        // Reset form
        setTitle("");
        setPrice("");
        setQuantity("");
        setSelectedFile(null);
      } else {
        console.error("Failed to create product:", error);
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to create product";
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setPrice("");
    setQuantity("");
    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="b2b-create-product-modal"
      PaperProps={{
        className: "b2b-modal-paper",
      }}
    >
      <DialogTitle className="b2b-modal-header">
        <Box className="b2b-header-content">
          <Typography variant="h5" component="div" className="b2b-modal-title">
            Create New Product
          </Typography>
        </Box>
        <Typography variant="body2" className="b2b-modal-subtitle">
          Add a new product for {selectedVendor?.name || selectedVendor?.title}
        </Typography>
      </DialogTitle>

      <DialogContent className="b2b-modal-content">
        <Box className="b2b-create-form">
          <TextField
            fullWidth
            label="Product Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            variant="outlined"
            required
            className="b2b-form-field"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            variant="outlined"
            type="number"
            required
            className="b2b-form-field"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            variant="outlined"
            type="number"
            required
            InputProps={{
              startAdornment: (
                <Inventory style={{ marginRight: 8, color: "#666" }} />
              ),
            }}
            className="b2b-form-field"
            sx={{ mb: 2 }}
          />

          {/* <Box className="b2b-image-upload" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Product Image
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              sx={{
                width: "100%",
                height: 56,
                borderColor: "#e0e0e0",
                color: "#666",
                "&:hover": {
                  borderColor: "#d32f2f",
                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                },
              }}
            >
              {selectedFile ? selectedFile.name : "Upload Image"}
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                sx={{ display: "none" }}
              />
            </Button>
          </Box> */}

          <Box className="b2b-vendor-category-info">
            <Typography variant="body2" color="text.secondary">
              <strong>Vendor:</strong>{" "}
              {selectedVendor?.name || selectedVendor?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Category:</strong> {selectedCategory?.name}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className="b2b-modal-actions">
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<Cancel />}
          disabled={loading}
          sx={{
            minWidth: 120,
            borderColor: "#000",
            color: "#000",
            "&:hover": {
              borderColor: "#333",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<CheckCircle />}
          disabled={loading || !title.trim() || !price || !quantity}
          sx={{
            minWidth: 120,
            backgroundColor: "#d32f2f",
            "&:hover": {
              backgroundColor: "#b71c1c",
            },
            "&:disabled": {
              backgroundColor: "#f5f5f5",
              color: "#bbb",
            },
          }}
        >
          {loading ? "Creating..." : "Create Product"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelfCreateProductModal;
